import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LogOut, GraduationCap, Loader2, ChevronRight, Goal, Waves, Timer, Bike, Dumbbell, Medal, Trophy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DISCIPLINA_ICONS = {
  futbol: Goal, natacion: Waves, atletismo: Timer, ciclismo: Bike,
  gimnasia: Dumbbell, artes_marciales: Medal, tenis: Trophy,
  basketball: Trophy, volleyball: Trophy, otro: GraduationCap
};

export default function StaffAcademiasDashboard({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [academias, setAcademias] = useState([]);

  useEffect(() => {
    loadMisAcademias();
  }, []);

  const loadMisAcademias = async () => {
    try {
      const res = await axios.get(`${API}/entrenador/mis-academias`, { withCredentials: true });
      const lista = res.data.academias || [];
      setAcademias(lista);
      
      // Si solo tiene acceso a una academia, redirigir directamente
      if (lista.length === 1) {
        navigate(`/academia/${lista[0].id}/entrenador`);
        return;
      }
    } catch (error) {
      console.error('Error loading academias:', error);
      toast.error('Error al cargar academias');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Sesión cerrada');
      window.location.href = '/';
    } catch (error) {
      toast.error('Error al cerrar sesión');
    }
  };

  const handleSelectAcademia = (academiaId) => {
    navigate(`/academia/${academiaId}/entrenador`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }

  // Sin academias asignadas
  if (academias.length === 0) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col">
        <header className="bg-slate-800 border-b border-slate-700 px-4 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-white">Portal Entrenador</h1>
                <p className="text-xs text-slate-400">{user?.name || user?.email}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center p-4">
          <Card className="p-8 bg-slate-800 border-slate-700 text-center max-w-md">
            <GraduationCap className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Sin Academias Asignadas</h2>
            <p className="text-slate-400 mb-6">
              No tienes acceso a ninguna academia en este momento. 
              Contacta al administrador de tu academia para recibir una invitación.
            </p>
            <Button onClick={handleLogout} variant="outline" className="border-slate-600 text-slate-300">
              Cerrar Sesión
            </Button>
          </Card>
        </main>
      </div>
    );
  }

  // Múltiples academias - mostrar selector
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">Portal Entrenador</h1>
              <p className="text-xs text-slate-400">{user?.name || user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white">
            <LogOut className="w-4 h-4 mr-2" />
            Salir
          </Button>
        </div>
      </header>

      <main className="flex-1 p-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-xl font-bold text-white mb-4">Selecciona una Academia</h2>
          <p className="text-slate-400 mb-6">
            Tienes acceso a {academias.length} academias. Selecciona una para continuar.
          </p>

          <div className="space-y-3">
            {academias.map((academia) => {
              const Icon = DISCIPLINA_ICONS[academia.disciplina] || GraduationCap;
              return (
                <Card
                  key={academia.id}
                  data-testid={`academia-card-${academia.id}`}
                  className="p-4 bg-slate-800 border-slate-700 hover:border-emerald-600 cursor-pointer transition-colors"
                  onClick={() => handleSelectAcademia(academia.id)}
                >
                  <div className="flex items-center gap-4">
                    {academia.logo ? (
                      <img src={academia.logo} alt="" className="w-14 h-14 rounded-lg object-cover" />
                    ) : (
                      <div className="w-14 h-14 bg-emerald-600/20 rounded-lg flex items-center justify-center">
                        <Icon className="w-7 h-7 text-emerald-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{academia.nombre}</h3>
                      <p className="text-sm text-slate-400">{academia.entrenador_nombre}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
